import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, LinearProgress, CircularProgress, Alert, Tooltip, IconButton,
  Select, MenuItem, FormControl, InputLabel, Chip, Switch, FormControlLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import EditableTable from '../components/common/EditableTable';
import OfferLinesEditor from '../components/common/OfferLinesEditor';
import RequestQuotesDialog from '../components/common/RequestQuotesDialog';
import QuoteComparison from '../components/common/QuoteComparison';
import { quoteColumn } from '../components/common/equipmentQuoteCells';
import PaymentConditionsEditor from '../components/common/PaymentConditionsEditor';
import {
  rawMaterialsAPI, processStagesAPI, utilitiesAPI, packagingSpecsAPI,
  equipmentsAPI, supplierOffersAPI, engineeringTasksAPI,
  engineeringAPI, configAPI, suppliersAPI, costEntriesAPI, documentsAPI,
} from '../services/api';

// ---------------------------------------------------------------------------
// Raw Materials
// ---------------------------------------------------------------------------
const rawMaterialColumns = [
  { field: 'name', header: 'Materia Prima', width: '18%' },
  { field: 'concentration', header: 'Concentracion' },
  { field: 'unit', header: 'Ud.' },
  { field: 'quantity_per_batch', header: 'Cant./Lote', type: 'number' },
  { field: 'supplier', header: 'Proveedor' },
  { field: 'cost_per_unit', header: 'Coste/Ud', type: 'number' },
];

const rawMaterialFields = [
  { field: 'name', label: 'Nombre materia prima', required: true },
  { field: 'cas_number', label: 'Numero CAS' },
  { field: 'concentration', label: 'Concentracion / Pureza' },
  { field: 'unit', label: 'Unidad (kg, L, etc.)' },
  { field: 'quantity_per_batch', label: 'Cantidad por lote', type: 'number' },
  { field: 'supplier', label: 'Proveedor' },
  { field: 'cost_per_unit', label: 'Coste por unidad (EUR)', type: 'number' },
  { field: 'storage_conditions', label: 'Condiciones almacenamiento' },
  { field: 'hazard_class', label: 'Clasificacion peligrosidad' },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

// ---------------------------------------------------------------------------
// Process Stages
// ---------------------------------------------------------------------------
const processStageColumns = [
  { field: 'name', header: 'Etapa', width: '20%' },
  { field: 'stage_type', header: 'Tipo' },
  { field: 'duration_minutes', header: 'Duracion (min)', type: 'number' },
  { field: 'temperature', header: 'Temperatura' },
  { field: 'critical_parameters', header: 'Parametros criticos' },
];

const processStageFields = [
  { field: 'name', label: 'Nombre de la etapa', required: true },
  { field: 'stage_type', label: 'Tipo de etapa', type: 'select', options: [
    { value: 'recepcion', label: 'Recepcion' }, { value: 'dosificacion', label: 'Dosificacion' },
    { value: 'mezclado', label: 'Mezclado' }, { value: 'calentamiento', label: 'Calentamiento' },
    { value: 'enfriamiento', label: 'Enfriamiento' }, { value: 'homogeneizacion', label: 'Homogeneizacion' },
    { value: 'filtracion', label: 'Filtracion' }, { value: 'control', label: 'Control calidad' },
    { value: 'envasado', label: 'Envasado' }, { value: 'almacenamiento', label: 'Almacenamiento' },
  ]},
  { field: 'duration_minutes', label: 'Duracion (minutos)', type: 'number' },
  { field: 'temperature', label: 'Temperatura de trabajo' },
  { field: 'pressure', label: 'Presion de trabajo' },
  { field: 'agitation_speed', label: 'Velocidad agitacion' },
  { field: 'input_materials', label: 'Materiales de entrada', fullWidth: true },
  { field: 'output_product', label: 'Producto de salida', fullWidth: true },
  { field: 'critical_parameters', label: 'Parametros criticos', multiline: true, rows: 2, fullWidth: true },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
const utilityColumns = [
  { field: 'name', header: 'Servicio', width: '18%' },
  { field: 'utility_type', header: 'Tipo' },
  { field: 'capacity_required', header: 'Capacidad' },
  { field: 'unit', header: 'Unidad' },
  { field: 'pressure', header: 'Presion' },
  { field: 'connection_point', header: 'Punto de acometida' },
];

const utilityFields = [
  { field: 'name', label: 'Nombre del servicio', required: true },
  { field: 'utility_type', label: 'Tipo', type: 'select', options: [
    { value: 'electrical', label: 'Electricidad' }, { value: 'water', label: 'Agua' },
    { value: 'steam', label: 'Vapor' }, { value: 'compressed_air', label: 'Aire comprimido' },
    { value: 'cooling', label: 'Agua de refrigeracion' }, { value: 'nitrogen', label: 'Nitrogeno' },
    { value: 'hot_water', label: 'Agua caliente' }, { value: 'wastewater', label: 'Aguas residuales' },
  ]},
  { field: 'capacity_required', label: 'Capacidad requerida' },
  { field: 'unit', label: 'Unidad (kW, m3/h, kg/h, Nm3/h)' },
  { field: 'pressure', label: 'Presion' },
  { field: 'temperature', label: 'Temperatura' },
  { field: 'connection_point', label: 'Punto de acometida', fullWidth: true },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

// ---------------------------------------------------------------------------
// Packaging
// ---------------------------------------------------------------------------
const packagingColumns = [
  { field: 'format_name', header: 'Formato', width: '18%' },
  { field: 'container_type', header: 'Envase' },
  { field: 'volume', header: 'Volumen' },
  { field: 'material', header: 'Material' },
  { field: 'closure_type', header: 'Cierre' },
  { field: 'production_speed', header: 'Velocidad' },
];

const defaultContainerOptions = [
  { value: 'botella', label: 'Botella' }, { value: 'garrafa', label: 'Garrafa' },
  { value: 'bidon', label: 'Bidon' }, { value: 'IBC', label: 'IBC' },
  { value: 'bag_in_box', label: 'Bag-in-box' }, { value: 'doypack', label: 'Doypack' },
  { value: 'tubo', label: 'Tubo' }, { value: 'tarro', label: 'Tarro' },
];

function getPackagingFields(containerOptions) {
  return [
    { field: 'format_name', label: 'Nombre del formato', required: true },
    { field: 'container_type', label: 'Tipo envase', type: 'select', options: containerOptions },
    { field: 'volume', label: 'Volumen' },
    { field: 'material', label: 'Material (PET, HDPE, vidrio...)' },
    { field: 'closure_type', label: 'Tipo de cierre' },
    { field: 'label_type', label: 'Tipo de etiqueta' },
    { field: 'production_speed', label: 'Velocidad produccion (env/h)' },
    { field: 'packing', label: 'Agrupacion (pack, caja, palet)', fullWidth: true },
    { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
  ];
}

// ---------------------------------------------------------------------------
// Lista de Equipos (unifica maquinas + equipos)
// ---------------------------------------------------------------------------
const equipmentColumns = [
  { field: 'tag', header: 'Tag', width: '8%' },
  { field: 'name', header: 'Equipo', width: '20%' },
  { field: 'capacity', header: 'Capacidad' },
  { field: 'material', header: 'Material' },
  { field: 'power', header: 'Potencia' },
  { field: 'supplier', header: 'Proveedor' },
  quoteColumn,
  { field: 'estimated_cost', header: 'Coste Real (EUR)', type: 'number' },
  { field: 'offer_cost', header: 'Coste Oferta (EUR)', type: 'number' },
  { field: 'delivery_weeks', header: 'Plazo (sem)', type: 'number' },
];

const equipmentFields = [
  { field: 'tag', label: 'Tag (ej: TK-001)' },
  { field: 'name', label: 'Nombre del equipo', required: true },
  { field: 'description', label: 'Descripcion', fullWidth: true },
  { field: 'capacity', label: 'Capacidad / Tamano' },
  { field: 'material', label: 'Material constructivo' },
  { field: 'power', label: 'Potencia instalada' },
  { field: 'dimensions', label: 'Dimensiones (LxWxH)' },
  { field: 'supplier', label: 'Proveedor' },
  { field: 'supplier_reference', label: 'Referencia proveedor' },
  { field: 'estimated_cost', label: 'Coste Real (EUR)', type: 'number' },
  { field: 'delivery_weeks', label: 'Plazo entrega (semanas)', type: 'number' },
  { field: 'notes', label: 'Notas / especificaciones', multiline: true, rows: 3, fullWidth: true },
];

// ---------------------------------------------------------------------------
// Supplier Offers
// ---------------------------------------------------------------------------
const COST_STATUS_OPTIONS = [
  { value: 'estimado',     label: 'Estimado' },
  { value: 'comprometido', label: 'Comprometido' },
  { value: 'facturado',    label: 'Facturado' },
  { value: 'cerrado',      label: 'Cerrado' },
];
const COST_STATUS_COLOR_MAP = {
  estimado:     'default',
  comprometido: 'warning',
  facturado:    'primary',
  cerrado:      'success',
};
const COST_STATUS_LABEL_MAP = {
  estimado:     'Estimado',
  comprometido: 'Comprometido',
  facturado:    'Facturado',
  cerrado:      'Cerrado',
};

const costColumns = [
  { field: 'concept', header: 'Concepto / Partida', width: '35%' },
  { field: 'real_cost_status', header: 'Estado', type: 'chip', width: '12%', colorMap: COST_STATUS_COLOR_MAP, labelMap: COST_STATUS_LABEL_MAP },
  { field: 'real_cost', header: 'Coste Real (EUR)', type: 'number' },
  { field: 'offer_cost', header: 'Coste Oferta (EUR)', type: 'number' },
];

// Estados posibles de una oferta de proveedor. Solo 'validated' y 'final' se
// consideran ofertas en firme: son las unicas que suman en la cabecera del
// listado y las unicas relevantes para pedidos/ordenes de fabricacion.
const SUPPLIER_OFFER_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendiente' }, { value: 'requested', label: 'Solicitada' },
  { value: 'received', label: 'Recibida' }, { value: 'validated', label: 'Validada' },
  { value: 'final', label: 'Definitiva' },
  // Los pone la licitacion, no se eligen a mano
  { value: 'not_awarded', label: 'No adjudicada' }, { value: 'superseded', label: 'Superada' },
];
const SUPPLIER_OFFER_STATUS_COLOR_MAP = {
  pending: 'default', requested: 'info', received: 'warning',
  validated: 'success', final: 'success',
  not_awarded: 'default', superseded: 'default',
};
const SUPPLIER_OFFER_STATUS_LABEL_MAP = {
  pending: 'Pendiente', requested: 'Solicitada', received: 'Recibida',
  validated: 'Validada', final: 'Definitiva',
  not_awarded: 'No adjudicada', superseded: 'Superada',
};
const SUPPLIER_OFFER_FIRM_STATUSES = ['validated', 'final'];

const supplierOfferColumns = [
  {
    field: 'equipment_name', header: 'Alcance', width: '20%',
    render: (row) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <span>{row.equipment_name || '-'}</span>
        {(row.revision || 1) > 1 && (
          <Chip size="small" label={`rev. ${row.revision}`} variant="outlined" sx={{ fontSize: '0.62rem', height: 18 }} />
        )}
        {row.rfq_code && (
          <Chip size="small" label={row.rfq_code} sx={{ fontSize: '0.62rem', height: 18 }} />
        )}
      </Box>
    ),
  },
  { field: 'supplier_name', header: 'Proveedor', width: '14%' },
  { field: 'status', header: 'Estado', type: 'chip', colorMap: SUPPLIER_OFFER_STATUS_COLOR_MAP, labelMap: SUPPLIER_OFFER_STATUS_LABEL_MAP },
  {
    field: 'lines_count', header: 'Partidas', width: '10%',
    render: (row) => {
      if (!row.lines_count) return '-';
      const parts = [`${row.quoted_count || 0}/${row.lines_count}`];
      if (row.pending_count) parts.push(`${row.pending_count} sin precio`);
      if (row.not_quoted_count) parts.push(`${row.not_quoted_count} no cotiza`);
      return (
        <Typography variant="caption" color={row.pending_count ? 'warning.main' : 'text.secondary'}>
          {parts.join(' · ')}
        </Typography>
      );
    },
  },
  { field: 'amount', header: 'Importe', type: 'number' },
  { field: 'delivery_weeks', header: 'Plazo (sem.)', type: 'number' },
  { field: 'reference', header: 'Referencia' },
];

const supplierOfferFields = [
  { field: 'equipment_name', label: 'Nombre del equipo', required: true },
  { field: 'supplier_name', label: 'Proveedor', required: true },
  { field: 'status', label: 'Estado', type: 'select', options: SUPPLIER_OFFER_STATUS_OPTIONS },
  { field: 'amount', label: 'Importe', type: 'number' },
  { field: 'delivery_weeks', label: 'Plazo de entrega (semanas)', type: 'number' },
  { field: 'reference', label: 'Referencia' },
  { field: 'is_obsolete', label: 'Oferta obsoleta / no procesable (no suma, no genera pedido)', type: 'checkbox', defaultValue: false, fullWidth: true },
  { field: 'offer_document_url', label: 'Documento de oferta (arrastra archivo o pega URL)', type: 'file_drop', fullWidth: true },
  { field: 'technical_notes', label: 'Notas tecnicas', multiline: true, rows: 3, fullWidth: true },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

// ---------------------------------------------------------------------------
// Engineering Tasks
// ---------------------------------------------------------------------------
const taskColumns = [
  { field: 'document_ref', header: 'Ref.', width: '8%' },
  { field: 'name', header: 'Tarea', width: '22%' },
  { field: 'discipline', header: 'Disciplina' },
  { field: 'assigned_to', header: 'Responsable' },
  { field: 'status', header: 'Estado', type: 'chip' },
  { field: 'progress', header: 'Avance', type: 'number' },
  { field: 'start_date', header: 'F. Inicio', type: 'date' },
  { field: 'weeks', header: 'Semanas', type: 'number' },
];

const taskFields = [
  { field: 'name', label: 'Nombre de la tarea', required: true, fullWidth: true },
  { field: 'discipline', label: 'Disciplina', type: 'select', options: [] },
  { field: 'assigned_to', label: 'Responsable' },
  { field: 'status', label: 'Estado', type: 'select', options: [
    { value: 'pending', label: 'Pendiente' }, { value: 'in_progress', label: 'En curso' },
    { value: 'review', label: 'En revision' }, { value: 'completed', label: 'Completado' },
  ]},
  { field: 'progress', label: 'Avance (%)', type: 'number' },
  { field: 'document_ref', label: 'Referencia documento' },
  { field: 'start_date', label: 'Fecha de inicio (planning)', type: 'date' },
  { field: 'weeks', label: 'Duración (semanas)', type: 'number' },
  { field: 'description', label: 'Descripcion detallada', multiline: true, rows: 3, fullWidth: true },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

const EMPTY_NOTES = {
  construction_notes: '', plc_programming_notes: '', scada_notes: '', commissioning_protocol: '',
};

// ---------------------------------------------------------------------------
// Offer Payment Conditions sub-component (loads on mount)
// ---------------------------------------------------------------------------
function OfferPaymentConditionsSection({ offer, conditions, onLoad, onAdd, onUpdate, onDelete, onCopyDefaults }) {
  React.useEffect(() => { onLoad(); }, [offer.id]); // eslint-disable-line
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
        Condiciones de pago
      </Typography>
      <Paper variant="outlined" sx={{ p: 1 }}>
        <PaymentConditionsEditor
          conditions={conditions}
          onAdd={onAdd}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onCopyDefaults={offer.supplier_id ? onCopyDefaults : undefined}
          amount={parseFloat(offer.amount) || 0}
        />
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function DetailEngineering({ projectId, projectType, projectDeliveryDate, approvedAmount }) {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [processStages, setProcessStages] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [packagingSpecs, setPackagingSpecs] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [supplierOffers, setSupplierOffers] = useState([]);
  const [showAllOffers, setShowAllOffers] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState(EMPTY_NOTES);
  const [costs, setCosts] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [engineeringCosts, setEngineeringCosts] = useState({ real_cost: null, offer_cost: null });
  const [offerPaymentConds, setOfferPaymentConds] = useState({});  // offerId -> conditions[]
  const [selectedEquipmentSupplier, setSelectedEquipmentSupplier] = useState('');
  const [containerOptions, setContainerOptions] = useState(defaultContainerOptions);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disciplineOptions, setDisciplineOptions] = useState([]);

  // Load disciplines from config
  useEffect(() => {
    configAPI.listDisciplines()
      .then((r) => setDisciplineOptions(r.data.map((d) => ({ value: d.name, label: d.name }))))
      .catch(() => {});
  }, []);

  // Build taskFields with dynamic discipline options
  const dynTaskFields = useMemo(() => taskFields.map((f) =>
    f.field === 'discipline' ? { ...f, options: disciplineOptions } : f
  ), [disciplineOptions]);

  useEffect(() => {
    if (projectId) loadAllData();
  }, [projectId]);

  useEffect(() => {
    if (!projectType) return;
    configAPI.listCostPartidas(projectType)
      .then((r) => setPartidas(r.data))
      .catch(() => {});
  }, [projectType]);

  useEffect(() => {
    suppliersAPI.list().then((resp) => {
      setSupplierOptions(resp.data.map((s) => ({ id: s.id, value: s.name, label: s.name })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    configAPI.listContainerTypes().then((resp) => {
      if (resp.data.length > 0) {
        setContainerOptions(resp.data.map((ct) => ({ value: ct.value, label: ct.label })));
      }
    }).catch(() => {});
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [rmResp, psResp, utResp, pkResp, eqResp, offResp, tsResp] = await Promise.all([
        rawMaterialsAPI.list(projectId),
        processStagesAPI.list(projectId),
        utilitiesAPI.list(projectId),
        packagingSpecsAPI.list(projectId),
        equipmentsAPI.list(projectId),
        supplierOffersAPI.list(projectId),
        engineeringTasksAPI.list(projectId),
      ]);
      setRawMaterials(rmResp.data);
      setProcessStages(psResp.data);
      setUtilities(utResp.data);
      setPackagingSpecs(pkResp.data);
      setEquipments(eqResp.data);
      setSupplierOffers(offResp.data);
      setTasks(tsResp.data);

      try {
        const ceResp = await costEntriesAPI.list(projectId);
        setCosts(ceResp.data);
      } catch { setCosts([]); }

      try {
        const dResp = await engineeringAPI.getDetail(projectId);
        setNotes({
          construction_notes: dResp.data.construction_notes || '',
          plc_programming_notes: dResp.data.plc_programming_notes || '',
          scada_notes: dResp.data.scada_notes || '',
          commissioning_protocol: dResp.data.commissioning_protocol || '',
        });
      } catch {
        await engineeringAPI.createDetail({ project_id: projectId });
      }

      try {
        const bResp = await engineeringAPI.getBasic(projectId);
        setEngineeringCosts({
          real_cost: bResp.data.real_cost ?? null,
          offer_cost: bResp.data.offer_cost ?? null,
        });
      } catch { /* basic_engineering may not exist yet */ }

      setError(null);
    } catch (err) {
      console.error('Error loading detail engineering data:', err);
      setError('Error al cargar los datos de ingenieria de detalle.');
    } finally {
      setLoading(false);
    }
  };

  const notesRef = React.useRef(notes);
  React.useEffect(() => { notesRef.current = notes; }, [notes]);

  const handleSaveNotes = async () => {
    const data = notesRef.current;
    try {
      setSaving(true);
      await engineeringAPI.updateDetail(projectId, {
        project_id: projectId,
        construction_notes: data.construction_notes || null,
        plc_programming_notes: data.plc_programming_notes || null,
        scada_notes: data.scada_notes || null,
        commissioning_protocol: data.commissioning_protocol || null,
      });
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Error al guardar notas.');
    } finally {
      setSaving(false);
    }
  };

  const handleNotesChange = (e) => setNotes({ ...notes, [e.target.name]: e.target.value });

  const costFields = [
    {
      field: 'concept', label: 'Concepto / Partida', required: true, fullWidth: true,
      type: 'autocomplete',
      options: partidas.map((p) => ({ value: p.name, label: p.name })),
    },
    { field: 'real_cost', label: 'Coste Real (EUR)', type: 'number' },
    {
      field: 'real_cost_status', label: 'Estado del coste real', type: 'select',
      options: COST_STATUS_OPTIONS,
    },
    { field: 'offer_cost', label: 'Coste Oferta (EUR)', type: 'number' },
    { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
  ];

  const costHandlers = {
    onAdd: async (data) => {
      try {
        await costEntriesAPI.create(projectId, data);
        const resp = await costEntriesAPI.list(projectId);
        setCosts(resp.data);
      } catch (err) { console.error('Error creating cost entry:', err); }
    },
    onUpdate: async (id, data) => {
      try {
        await costEntriesAPI.update(id, data);
        const resp = await costEntriesAPI.list(projectId);
        setCosts(resp.data);
      } catch (err) { console.error('Error updating cost entry:', err); }
    },
    onDelete: async (id) => {
      try {
        await costEntriesAPI.delete(id);
        setCosts(costs.filter((i) => i.id !== id));
      } catch (err) { console.error('Error deleting cost entry:', err); }
    },
  };

  const addPartidaAsCost = async (partidaName) => {
    if (costs.some((c) => c.concept === partidaName)) return;
    try {
      await costEntriesAPI.create(projectId, { concept: partidaName, real_cost: null, offer_cost: null, notes: null });
      const resp = await costEntriesAPI.list(projectId);
      setCosts(resp.data);
    } catch (err) { console.error('Error adding partida:', err); }
  };

  const addAllPartidas = async () => {
    const existing = new Set(costs.map((c) => c.concept));
    const toAdd = partidas.filter((p) => !existing.has(p.name));
    for (const p of toAdd) {
      try {
        await costEntriesAPI.create(projectId, { concept: p.name, real_cost: null, offer_cost: null, notes: null });
      } catch (err) { console.error('Error adding partida:', err); }
    }
    const resp = await costEntriesAPI.list(projectId);
    setCosts(resp.data);
  };

  const handleRefreshCosts = async () => {
    try {
      const bResp = await engineeringAPI.getBasic(projectId);
      setEngineeringCosts({
        real_cost: bResp.data.real_cost ?? null,
        offer_cost: bResp.data.offer_cost ?? null,
      });
    } catch (err) {
      console.error('Error al refrescar costes:', err);
    }
  };

  const makeHandlers = (apiModule, setter) => ({
    onAdd: async (data) => {
      try { await apiModule.create(projectId, data); const r = await apiModule.list(projectId); setter(r.data); }
      catch (err) { console.error('Error creating:', err); }
    },
    onUpdate: async (id, data) => {
      try { await apiModule.update(id, data); const r = await apiModule.list(projectId); setter(r.data); }
      catch (err) { console.error('Error updating:', err); }
    },
    onDelete: async (id) => {
      try { await apiModule.delete(id); const r = await apiModule.list(projectId); setter(r.data); }
      catch (err) { console.error('Error deleting:', err); }
    },
  });

  // --- Offer Payment Conditions ---
  const loadOfferPaymentConds = async (offerId) => {
    try {
      const resp = await supplierOffersAPI.listPaymentConditions(offerId);
      setOfferPaymentConds((prev) => ({ ...prev, [offerId]: resp.data }));
    } catch (e) { console.error('Error loading offer payment conditions:', e); }
  };

  const reloadEquipmentsAndOffers = async () => {
    const [eqResp, offResp] = await Promise.all([
      equipmentsAPI.list(projectId),
      supplierOffersAPI.list(projectId),
    ]);
    setEquipments(eqResp.data);
    setSupplierOffers(offResp.data);
  };

  const renderOfferPaymentConditions = (offer) => (
    <OfferPaymentConditionsSection
      offer={offer}
      conditions={offerPaymentConds[offer.id] || []}
      onLoad={() => loadOfferPaymentConds(offer.id)}
      onAdd={async (data) => {
        await supplierOffersAPI.createPaymentCondition(offer.id, data);
        await loadOfferPaymentConds(offer.id);
      }}
      onUpdate={async (id, data) => {
        await supplierOffersAPI.updatePaymentCondition(id, data);
        await loadOfferPaymentConds(offer.id);
      }}
      onDelete={async (id) => {
        await supplierOffersAPI.deletePaymentCondition(id);
        await loadOfferPaymentConds(offer.id);
      }}
      onCopyDefaults={async () => {
        await supplierOffersAPI.copySupplierDefaults(offer.id);
        await loadOfferPaymentConds(offer.id);
      }}
    />
  );

  // Al soltar la oferta se archiva en la carpeta del proyecto, bajo
  // INGENIERIA/OFERTA PROVEEDORES/<proveedor>, y se guarda el enlace resultante.
  // Si no se puede archivar, el campo conserva igualmente la referencia.
  const fileOfferDocument = async (file, form) => {
    const resp = await documentsAPI.uploadSupplierOffer(
      projectId, form?.supplier_name || '', file,
    );
    return resp.data;
  };

  const renderOfferDetail = (offer) => (
    <>
      <OfferLinesEditor
        offer={offer}
        equipments={equipments}
        onChanged={reloadEquipmentsAndOffers}
      />
      {renderOfferPaymentConditions(offer)}
    </>
  );

  const disciplines = [...new Set(tasks.map((t) => t.discipline).filter(Boolean))];
  const totalProgress = tasks.length > 0
    ? (tasks.reduce((sum, t) => sum + (parseFloat(t.progress) || 0), 0) / tasks.length).toFixed(0)
    : 0;

  if (!projectId) return <Alert severity="warning">No se ha seleccionado un proyecto.</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Banner costes */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#c62828', color: '#fff', display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#fff' }}>Costes del Proyecto:</Typography>
        <Box>
          <Typography variant="caption" sx={{ color: '#fff' }}>Coste Real</Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
            {engineeringCosts.real_cost !== null
              ? Number(engineeringCosts.real_cost).toLocaleString('es-ES', { minimumFractionDigits: 2 }) + ' EUR'
              : '— EUR'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#fff' }}>Coste Oferta</Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
            {engineeringCosts.offer_cost !== null
              ? Number(engineeringCosts.offer_cost).toLocaleString('es-ES', { minimumFractionDigits: 2 }) + ' EUR'
              : '— EUR'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#fff' }}>Importe Venta</Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
            {approvedAmount !== null && approvedAmount !== undefined
              ? Number(approvedAmount).toLocaleString('es-ES', { minimumFractionDigits: 2 }) + ' EUR'
              : '— EUR'}
          </Typography>
        </Box>
        <Tooltip title="Recargar costes desde Ingenier\u00eda B\u00e1sica">
          <IconButton sx={{ color: '#fff' }} onClick={handleRefreshCosts}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ color: '#fff', opacity: 0.85 }}>(definidos en Ingenieria Basica — usa Refrescar en Ing. Basica para actualizar)</Typography>
      </Paper>

      <EditableTable
        title="Materias Primas"
        columns={rawMaterialColumns} rows={rawMaterials} dialogFields={rawMaterialFields}
        emptyMessage="No hay materias primas definidas. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeHandlers(rawMaterialsAPI, setRawMaterials)}
      />

      <EditableTable
        title="Etapas del Proceso"
        columns={processStageColumns} rows={processStages} dialogFields={processStageFields}
        emptyMessage="No hay etapas definidas. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeHandlers(processStagesAPI, setProcessStages)}
      />

      <EditableTable
        title="Utilities / Servicios Auxiliares"
        columns={utilityColumns} rows={utilities} dialogFields={utilityFields}
        emptyMessage="No hay utilities definidos. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeHandlers(utilitiesAPI, setUtilities)}
      />

      <EditableTable
        title="Formatos de Envasado"
        columns={packagingColumns} rows={packagingSpecs} dialogFields={getPackagingFields(containerOptions)}
        emptyMessage="No hay formatos de envasado definidos. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeHandlers(packagingSpecsAPI, setPackagingSpecs)}
      />

      <EditableTable
        title="Lista de Equipos"
        columns={equipmentColumns} rows={equipments}
        notesField="notes"
        collapsible defaultCollapsed
        selectable
        selectedIds={selectedEquipmentIds}
        onSelectionChange={setSelectedEquipmentIds}
        headerExtra={
          <Button
            size="small" variant="contained" startIcon={<RequestQuoteIcon />}
            disabled={selectedEquipmentIds.length === 0}
            onClick={() => setRfqOpen(true)}
          >
            Pedir oferta{selectedEquipmentIds.length > 0 ? ` (${selectedEquipmentIds.length})` : ''}
          </Button>
        }
        summaryBanner={(() => {
          const uniqueSuppliers = [...new Set(equipments.map((e) => e.supplier).filter(Boolean))].sort();
          const filtered = selectedEquipmentSupplier ? equipments.filter((e) => e.supplier === selectedEquipmentSupplier) : [];
          const sumReal = filtered.reduce((s, e) => s + (parseFloat(e.estimated_cost) || 0), 0);
          const sumOffer = filtered.reduce((s, e) => s + (parseFloat(e.offer_cost) || 0), 0);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
              {uniqueSuppliers.length > 0 && (
                <>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filtrar por proveedor</InputLabel>
                    <Select
                      value={selectedEquipmentSupplier}
                      label="Filtrar por proveedor"
                      onChange={(e) => setSelectedEquipmentSupplier(e.target.value)}
                    >
                      <MenuItem value=""><em>Todos</em></MenuItem>
                      {uniqueSuppliers.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedEquipmentSupplier && (
                    <Paper sx={{ px: 2, py: 0.5, backgroundColor: '#c62828', color: '#fff', display: 'flex', gap: 3, alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#fff' }}>Coste Real</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                          {sumReal.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#fff' }}>Coste Oferta</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                          {sumOffer.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </>
              )}
            </Box>
          );
        })()}
        dialogFields={equipmentFields.map((f) =>
          f.field === 'supplier'
            ? { ...f, type: 'autocomplete', options: supplierOptions }
            : f
        )}
        emptyMessage="No hay equipos definidos. Haz clic en Anadir."
        {...makeHandlers(equipmentsAPI, setEquipments)}
      />

      <RequestQuotesDialog
        open={rfqOpen}
        onClose={() => setRfqOpen(false)}
        projectId={projectId}
        equipments={equipments.filter((e) => selectedEquipmentIds.includes(e.id))}
        supplierOptions={supplierOptions}
        onCreated={async () => { setSelectedEquipmentIds([]); await reloadEquipmentsAndOffers(); }}
      />

      <EditableTable
        title="Ofertas de Proveedores"
        columns={supplierOfferColumns}
        rows={showAllOffers
          ? supplierOffers
          : supplierOffers.filter((o) => !o.is_obsolete && o.status !== 'superseded')}
        collapsible defaultCollapsed
        headerExtra={
          <FormControlLabel
            control={<Switch size="small" checked={showAllOffers} onChange={(e) => setShowAllOffers(e.target.checked)} />}
            label={<Typography variant="caption">Ver todas las ofertas</Typography>}
            sx={{ mr: 0 }}
          />
        }
        summaryBanner={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {supplierOffers.length > 0 && (
              <Paper sx={{ px: 2, py: 0.5, backgroundColor: '#c62828', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#fff' }}>Total (validadas/definitivas):</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                  {supplierOffers
                    .filter((o) => !o.is_obsolete && SUPPLIER_OFFER_FIRM_STATUSES.includes(o.status))
                    .reduce((s, o) => s + (parseFloat(o.amount) || 0), 0)
                    .toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
                </Typography>
              </Paper>
            )}
            <Chip size="small" label="🟢 Pedido realizado" sx={{ backgroundColor: '#e8f5e9', fontSize: '0.72rem' }} />
            <Chip size="small" label="🟠 Hay que hacer pedido" sx={{ backgroundColor: '#fff3e0', fontSize: '0.72rem' }} />
            <Chip size="small" label="🔴 No llegamos a tiempo" sx={{ backgroundColor: '#ffebee', fontSize: '0.72rem' }} />
            <Chip size="small" label="🟥 Obsoleta / no procesable" sx={{ backgroundColor: '#ffcdd2', fontSize: '0.72rem' }} />
          </Box>
        }
        dialogFields={(item) => supplierOfferFields.map((f) => {
          if (f.field === 'supplier_name') {
            return { ...f, type: 'autocomplete', options: supplierOptions };
          }
          if (f.field === 'offer_document_url') {
            return { ...f, onUpload: fileOfferDocument };
          }
          // En cuanto la oferta tiene partidas, el importe se recalcula solo a partir
          // de ellas (_recalc_offer_amount en el backend) y el editor de partidas se
          // muestra en el mismo dialogo (renderExtraContent, mas abajo). Si se deja
          // editable aqui, guardar el dialogo reenvia el importe que habia al abrirlo
          // y -con una sola partida- ese valor obsoleto pisa el precio que se acaba de
          // cambiar ahi mismo. Por eso se deshabilita en cuanto hay alguna partida, no
          // solo con mas de una.
          if (f.field === 'amount' && item && item.pricing_mode !== 'lump'
            && ((item.lines_count || 0) > 0 || item.has_purchase_order)) {
            return { ...f, disabled: true, helperText: 'Se calcula solo a partir de las partidas: se edita ahi.' };
          }
          return f;
        })}
        documentLinkField="offer_document_url"
        emptyMessage="No hay ofertas registradas. Haz clic en Anadir."
        getRowSx={(row) => {
          if (row.is_obsolete) return { backgroundColor: '#ef9a9a' };
          if (row.status === 'superseded') return { backgroundColor: '#eeeeee', opacity: 0.7 };
          if (row.status === 'not_awarded') return { backgroundColor: '#f5f5f5', opacity: 0.85 };
          if (row.has_purchase_order) return { backgroundColor: '#e8f5e9' };
          if (!row.delivery_weeks || !projectDeliveryDate) return {};
          const today = new Date();
          const eta = new Date(today);
          eta.setDate(eta.getDate() + row.delivery_weeks * 7);
          const delivDate = new Date(projectDeliveryDate);
          const diffDays = (delivDate - eta) / (1000 * 60 * 60 * 24);
          if (diffDays < 0) return { backgroundColor: '#ffebee' };
          if (diffDays <= 21) return { backgroundColor: '#fff3e0' };
          return {};
        }}
        {...makeHandlers(supplierOffersAPI, setSupplierOffers)}
        renderExtraContent={renderOfferDetail}
      />

      <QuoteComparison projectId={projectId} onChanged={reloadEquipmentsAndOffers} />

      {/* Partidas de coste configurables */}
      {partidas.length > 0 && (
        <Paper sx={{ p: 2, mt: 3, mb: 1, backgroundColor: '#c62828', color: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff' }}>
              Partidas disponibles para este tipo de proyecto:
            </Typography>
            <Button size="small" variant="outlined" onClick={addAllPartidas} sx={{ color: '#fff', borderColor: '#fff', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
              Anadir todas las partidas
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {partidas.map((p) => {
              const alreadyAdded = costs.some((c) => c.concept === p.name);
              return (
                <Chip
                  key={p.id}
                  label={p.name}
                  size="small"
                  color={alreadyAdded ? 'success' : 'default'}
                  variant={alreadyAdded ? 'filled' : 'outlined'}
                  onClick={alreadyAdded ? undefined : () => addPartidaAsCost(p.name)}
                  sx={{ cursor: alreadyAdded ? 'default' : 'pointer' }}
                />
              );
            })}
          </Box>
        </Paper>
      )}

      <EditableTable
        title="Costes por Partida"
        columns={costColumns}
        rows={costs}
        dialogFields={costFields}
        emptyMessage="No hay partidas de coste. Haz clic en Anadir o selecciona de la lista superior."
        collapsible defaultCollapsed
        summaryBanner={costs.length > 0 ? (
          <Paper sx={{ px: 2, py: 0.5, backgroundColor: '#c62828', color: '#fff', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>Coste Real:</Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                {costs.reduce((s, c) => s + (parseFloat(c.real_cost) || 0), 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#fff' }}>Coste Oferta:</Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ color: '#fff' }}>
                {costs.reduce((s, c) => s + (parseFloat(c.offer_cost) || 0), 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR
              </Typography>
            </Box>
          </Paper>
        ) : null}
        {...costHandlers}
      />

      {/* Progress Section */}
      <Paper sx={{ p: 3, mt: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Progreso General del Proyecto</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={parseFloat(totalProgress)}
            sx={{ flexGrow: 1, height: 12, borderRadius: 6 }}
          />
          <Typography variant="h6" fontWeight="bold">{totalProgress}%</Typography>
        </Box>
        <Grid container spacing={2}>
          {disciplines.map((d) => {
            const dTasks = tasks.filter((t) => t.discipline === d);
            const dProgress = (
              dTasks.reduce((s, t) => s + (parseFloat(t.progress) || 0), 0) / dTasks.length
            ).toFixed(0);
            return (
              <Grid item xs={6} sm={4} md={3} key={d}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">{d}</Typography>
                  <Typography variant="h6" fontWeight="bold">{dProgress}%</Typography>
                  <LinearProgress variant="determinate" value={parseFloat(dProgress)} sx={{ height: 6, borderRadius: 3 }} />
                  <Typography variant="caption">{dTasks.length} tareas</Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <EditableTable
        title="Tareas de Ingenieria"
        columns={taskColumns} rows={tasks} dialogFields={dynTaskFields}
        emptyMessage="No hay tareas definidas. Haz clic en Anadir."
        collapsible defaultCollapsed
        {...makeHandlers(engineeringTasksAPI, setTasks)}
      />

      {/* Notes Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>Notas de Ejecucion</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Notas de construccion / montaje"
              name="construction_notes" value={notes.construction_notes} onChange={handleNotesChange} onBlur={handleSaveNotes} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Notas PLC / programacion"
              name="plc_programming_notes" value={notes.plc_programming_notes} onChange={handleNotesChange} onBlur={handleSaveNotes} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Notas SCADA / supervision"
              name="scada_notes" value={notes.scada_notes} onChange={handleNotesChange} onBlur={handleSaveNotes} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Protocolo de puesta en marcha"
              name="commissioning_protocol" value={notes.commissioning_protocol} onChange={handleNotesChange} onBlur={handleSaveNotes} />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
